/**
 * Voicemail API service functions
 * Aligned with dashboard API call patterns
 */
export const voicemailService = {
  /**
   * Fetch all voicemail recordings
   * @param {number} page - Page number (default: 1)
   * @param {string} status - Filter by status: 'unread', 'read', or 'all' (default: 'all')
   * @returns {Promise<Array>} Array of voicemail recordings
   */
  async fetchVoicemails(page = 1, status = 'all') {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = '/voicemail-recording';
      let url = `${baseUrl}${endpoint}?page=${page}`;
      
      // Add status filter if not 'all'
      if (status && status !== 'all') {
        url += `&status=${status}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemails: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle the actual API response structure with pagination
      if (data.success && data.data && data.data.data) {
        return {
          voicemails: data.data.data,
          pagination: {
            current_page: data.data.current_page,
            total: data.data.total,
            per_page: data.data.per_page,
            last_page: data.data.last_page,
            from: data.data.from,
            to: data.data.to,
            first_page_url: data.data.first_page_url,
            last_page_url: data.data.last_page_url,
            next_page_url: data.data.next_page_url,
            prev_page_url: data.data.prev_page_url,
            path: data.data.path,
            links: data.data.links
          }
        };
      }
      
      // Fallback for different response structures
      return data.data || data || [];
    } catch (error) {
      console.error('Error fetching voicemails:', error);
      throw error;
    }
  },

  /**
   * Fetch a specific voicemail recording by ID
   * @param {string|number} id - The voicemail recording ID
   * @returns {Promise<Object>} Voicemail recording details
   */
  async fetchVoicemailById(id) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/${id}`, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemail: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching voicemail by ID:', error);
      throw error;
    }
  },

  /**
   * Get authenticated Twilio recording URL
   * @param {string} recordingSid - The Twilio recording SID
   * @returns {Promise<string>} Authenticated recording URL
   */
  async getAuthenticatedRecordingUrl(recordingSid) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/${recordingSid}/audio`, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get authenticated recording URL: ${response.status}`);
      }

      const data = await response.json();
      return data.authenticated_url || data.url || data;
    } catch (error) {
      console.error('Error getting authenticated recording URL:', error);
      throw error;
    }
  },

  /**
   * Try to get a playable Twilio recording URL
   * @param {string} recordingSid - The Twilio recording SID
   * @param {string} originalUrl - The original Twilio URL
   * @returns {Promise<string>} Playable recording URL
   */
  async getPlayableRecordingUrl(recordingSid, originalUrl) {
    try {
      // First try to get authenticated URL
      return await this.getAuthenticatedRecordingUrl(recordingSid);
    } catch (error) {
      console.warn('Authenticated URL failed, trying alternative approaches:', error);
      
      // Try to convert to .mp3 format (sometimes works)
      if (originalUrl && originalUrl.includes('api.twilio.com')) {
        const mp3Url = originalUrl.replace(/\.json$/, '.mp3');
        return mp3Url;
      }
      
      // Return original URL as fallback
      return originalUrl;
    }
  },

  /**
   * Create a new voicemail recording
   * @param {Object} voicemailData - The voicemail data to create
   * @returns {Promise<Object>} Created voicemail recording
   */
  async createVoicemail(voicemailData) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording`, {
        method: 'POST',
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(voicemailData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create voicemail: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error creating voicemail:', error);
      throw error;
    }
  },

  /**
   * Update voicemail recording status
   * @param {string|number} id - The voicemail recording ID
   * @param {string|Object} statusOrData - The new status (e.g., 'read', 'unread', 'archived') or an object with status, read, email_sent, notes, etc.
   * @returns {Promise<Object>} Updated voicemail recording
   */
  async updateVoicemailStatus(id, statusOrData) {
    try {
      // Support both old format (string status) and new format (object with multiple fields)
      const body = typeof statusOrData === 'string' 
        ? { status: statusOrData }
        : statusOrData;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to update voicemail status: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error updating voicemail status:', error);
      throw error;
    }
  },

  /**
   * Update voicemail recording (full update)
   * @param {string|number} id - The voicemail recording ID
   * @param {Object} voicemailData - The voicemail data to update
   * @returns {Promise<Object>} Updated voicemail recording
   */
  async updateVoicemail(id, voicemailData) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(voicemailData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update voicemail: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error updating voicemail:', error);
      throw error;
    }
  },

  /**
   * Delete voicemail recording
   * @param {string|number} id - The voicemail recording ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteVoicemail(id) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete voicemail: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting voicemail:', error);
      throw error;
    }
  },

  /**
   * Search voicemails with filters
   * @param {Object} filters - Search filters (status, dateRange, callerName, etc.)
   * @returns {Promise<Array>} Filtered voicemail recordings
   */
  async searchVoicemails(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/voicemail-recording/search?${queryParams}`, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to search voicemails: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data || [];
    } catch (error) {
      console.error('Error searching voicemails:', error);
      throw error;
    }
  },

  /**
   * Fetch voicemail metrics (overall metrics)
   * @param {Object} params - Query parameters (start_date, end_date, twilio_number)
   * @returns {Promise<Object>} Voicemail metrics
   */
  async fetchVoicemailMetrics(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.twilio_number) queryParams.append('twilio_number', params.twilio_number);

      const url = `${process.env.REACT_APP_API_URL}/voicemail-recording/metrics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemail metrics: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voicemail metrics:', error);
      throw error;
    }
  },

  /**
   * Fetch voicemail status report (breakdown by status)
   * @param {Object} params - Query parameters (start_date, end_date, twilio_number)
   * @returns {Promise<Object>} Status breakdown report
   */
  async fetchVoicemailStatusReport(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.twilio_number) queryParams.append('twilio_number', params.twilio_number);

      const url = `${process.env.REACT_APP_API_URL}/voicemail-recording/report/status${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemail status report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voicemail status report:', error);
      throw error;
    }
  },

  /**
   * Fetch voicemail category report (breakdown by category)
   * @param {Object} params - Query parameters (start_date, end_date, twilio_number)
   * @returns {Promise<Object>} Category breakdown report
   */
  async fetchVoicemailCategoryReport(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.twilio_number) queryParams.append('twilio_number', params.twilio_number);

      const url = `${process.env.REACT_APP_API_URL}/voicemail-recording/report/category${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemail category report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voicemail category report:', error);
      throw error;
    }
  },

  /**
   * Fetch voicemail daily report (daily breakdown)
   * @param {Object} params - Query parameters (start_date, end_date, twilio_number)
   * @returns {Promise<Object>} Daily breakdown report
   */
  async fetchVoicemailDailyReport(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.twilio_number) queryParams.append('twilio_number', params.twilio_number);

      const url = `${process.env.REACT_APP_API_URL}/voicemail-recording/report/daily${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTHORIZATION,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch voicemail daily report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voicemail daily report:', error);
      throw error;
    }
  },
};
